import { Injectable } from '@angular/core';
import { IParametreTumu } from '../../../../interfaces/IParametre/IParametre';

export interface ValidasyonSonuc {
    gecerli: boolean;
    hatalar: string[];
    uyarilar: string[];
}

export interface ValidasyonKurali {
    ad: string;
    kontrol: (deger: any, parametre: IParametreTumu, tumParametreler?: IParametreTumu[]) => string | null;
    zorunluMu?: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class ParametreValidationService {
  private kurallar: { [tur: string]: ValidasyonKurali[] } = {};

  constructor() {
    this.kurallariBaslat();
  }

  private kurallariBaslat(): void {
    // Yazı için validasyon kuralları
    this.kurallar['Yazı'] = [
      {
        ad: 'required',
        kontrol: (deger, parametre) => {
          if (parametre.ayarlar?.zorunlu && (!deger || deger.toString().trim() === '')) {
            return 'Bu alan zorunludur';
          }
          return null;
        },
        zorunluMu: false
      },
      {
        ad: 'minLength',
        kontrol: (deger, parametre) => {
          const min = parametre.ayarlar?.minLength || parametre.ayarlar?.minKarakter;
          if (min && deger && deger.toString().length < min) {
            return `En az ${min} karakter olmalıdır`;
          }
          return null;
        }
      },
      {
        ad: 'maxLength',
        kontrol: (deger, parametre) => {
          const max = parametre.ayarlar?.maxLength || parametre.ayarlar?.maxKarakter;
          if (max && deger && deger.toString().length > max) {
            return `En fazla ${max} karakter olmalıdır`;
          }
          return null;
        }
      },
      {
        ad: 'regex',
        kontrol: (deger, parametre) => {
          const regex = parametre.ayarlar?.regex || parametre.ayarlar?.desen;
          if (regex && deger) {
            try {
              const pattern = new RegExp(regex);
              if (!pattern.test(deger.toString())) {
                return 'Geçersiz format';
              }
            } catch (e) {
              return 'Geçersiz regex deseni';
            }
          }
          return null;
        }
      }
    ];

    // Sayı için validasyon kuralları
    this.kurallar['Sayı'] = [
      {
        ad: 'required',
        kontrol: (deger, parametre) => {
          if (parametre.ayarlar?.zorunlu && (deger === null || deger === undefined || deger === '')) {
            return 'Bu alan zorunludur';
          }
          return null;
        }
      },
      {
        ad: 'min',
        kontrol: (deger, parametre) => {
          const min = parametre.ayarlar?.min;
          if (min !== undefined && deger !== null && deger !== undefined && !(deger instanceof Date) && typeof min === 'number' && Number(deger) < min) {
            return `Değer ${min}'den küçük olamaz`;
          }
          return null;
        }
      },
      {
        ad: 'max',
        kontrol: (deger, parametre) => {
          const max = parametre.ayarlar?.max;
          if (max !== undefined && deger !== null && deger !== undefined && !(deger instanceof Date) && typeof max === 'number' && Number(deger) > max) {
            return `Değer ${max}'dan büyük olamaz`;
          }
          return null;
        }
      },
      {
        ad: 'integer',
        kontrol: (deger, parametre) => {
          if (parametre.ayarlar?.integer && deger !== null && deger !== undefined && !Number.isInteger(Number(deger))) {
            return 'Tam sayı olmalıdır';
          }
          return null;
        }
      },
      {
        ad: 'positive',
        kontrol: (deger, parametre) => {
          if (parametre.ayarlar?.positive && deger !== null && deger !== undefined && Number(deger) <= 0) {
            return 'Pozitif olmalıdır';
          }
          return null;
        }
      }
    ];

    // E-posta için validasyon
    this.kurallar['E-posta'] = [
      {
        ad: 'required',
        kontrol: (deger, parametre) => {
          if (parametre.ayarlar?.zorunlu && (!deger || deger.toString().trim() === '')) {
            return 'E-posta adresi zorunludur';
          }
          return null;
        }
      },
      {
        ad: 'email',
        kontrol: (deger, parametre) => {
          if (deger && deger.toString().trim()) {
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            if (!emailRegex.test(deger.toString().trim())) {
              return 'Geçerli bir e-posta adresi giriniz';
            }
          }
          return null;
        }
      }
    ];
  }

  parametreValidasyon(parametre: IParametreTumu, tumParametreler?: IParametreTumu[]): ValidasyonSonuc {
    const sonuc: ValidasyonSonuc = {
      gecerli: true,
      hatalar: [],
      uyarilar: []
    };

    if (!parametre || !parametre.turu) {
      return sonuc;
    }

    const turKurallari = this.kurallar[parametre.turu] || [];

    for (const kural of turKurallari) {
      try {
        const hata = kural.kontrol(parametre.degeri, parametre, tumParametreler);
        if (hata) {
          sonuc.hatalar.push(hata);
          sonuc.gecerli = false;
        }
      } catch (e) {
        sonuc.uyarilar.push(`${kural.ad} validasyonu sırasında hata oluştu`);
      }
    }

    // Özel validasyon kontrolleri
    if ((parametre as any).ozelValidasyon) {
      try {
        const ozelHata = this.ozelValidasyonCalistir((parametre as any).ozelValidasyon, parametre, tumParametreler);
        if (ozelHata) {
          sonuc.hatalar.push(ozelHata);
          sonuc.gecerli = false;
        }
      } catch (e) {
        sonuc.uyarilar.push('Özel validasyon çalıştırılırken hata oluştu');
      }
    }

    return sonuc;
  }

  private ozelValidasyonCalistir(validasyonKodu: string, parametre: IParametreTumu, tumParametreler?: IParametreTumu[]): string | null {
    try {
      // Güvenli validasyon fonksiyonu
      const validasyonFonksiyonu = new Function('deger', 'parametre', 'tumParametreler', validasyonKodu);
      return validasyonFonksiyonu(parametre.degeri, parametre, tumParametreler);
    } catch (e) {
      return 'Geçersiz validasyon kodu';
    }
  }

  tumParametreleriValidasyon(parametreler: IParametreTumu[]): { gecerli: boolean; hataliParametreler: IParametreTumu[] } {
    const hataliParametreler: IParametreTumu[] = [];
    let tumuGecerli = true;

    for (const parametre of parametreler || []) {
      const sonuc = this.parametreValidasyon(parametre, parametreler);
      if (!sonuc.gecerli) {
        hataliParametreler.push(parametre);
        tumuGecerli = false;
      }
    }

    return {
      gecerli: tumuGecerli,
      hataliParametreler
    };
  }

  kuralEkle(tur: string, kural: ValidasyonKurali): void {
    if (!this.kurallar[tur]) {
      this.kurallar[tur] = [];
    }
    this.kurallar[tur].push(kural);
  }

  kuralKaldir(tur: string, kuralAdi: string): boolean {
    if (!this.kurallar[tur]) {
      return false;
    }

    const index = this.kurallar[tur].findIndex(k => k.ad === kuralAdi);
    if (index !== -1) {
      this.kurallar[tur].splice(index, 1);
      return true;
    }

    return false;
  }

  desteklenenTurler(): string[] {
    return Object.keys(this.kurallar);
  }

  turKurallari(tur: string): ValidasyonKurali[] {
    return this.kurallar[tur] || [];
  }
}